import * as THREE from "three";

/**
 * Снаряд в 3D (магический выстрел, Джон 04.11.2026, 22:29: «сгусток летит через арену»): яркое ядро, мягкий ореол,
 * хвост искр позади и свой свет — снаряд подсвечивает бойцов и пол, пока летит. Всё от кадра мира и положения снаряда.
 */
export interface ShotView {
  readonly live: boolean;
  /** Центр снаряда, пиксели мира (y — вверх от пола), скорость по x (знак — куда летит), кадров в полёте. */
  readonly x: number;
  readonly y: number;
  readonly vx: number;
  readonly age: number;
}

const TRAIL = 48;
const CORE = 0xe8eeff;
const GLOW = 0x8a6aff;

function glowTexture(): THREE.CanvasTexture {
  const canvas = document.createElement("canvas");
  canvas.width = 64;
  canvas.height = 64;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Canvas 2D недоступен");
  const gradient = context.createRadialGradient(32, 32, 0, 32, 32, 32);
  gradient.addColorStop(0, "rgba(255,255,255,1)");
  gradient.addColorStop(0.35, "rgba(170,140,255,0.7)");
  gradient.addColorStop(1, "rgba(90,60,255,0)");
  context.fillStyle = gradient;
  context.fillRect(0, 0, 64, 64);
  return new THREE.CanvasTexture(canvas);
}

const hash = (value: number): number => {
  let h = Math.imul(value ^ 0x9e3779b9, 0x85ebca6b);
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35);
  return (h ^ (h >>> 16)) >>> 0;
};

export class Shot3D {
  private readonly core: THREE.Mesh;
  private readonly halo: THREE.Sprite;
  private readonly light: THREE.PointLight;
  private readonly trail: THREE.Points;
  private readonly positions = new Float32Array(TRAIL * 3);

  constructor(scene: THREE.Scene) {
    this.core = new THREE.Mesh(
      new THREE.SphereGeometry(9, 16, 12),
      new THREE.MeshBasicMaterial({ color: CORE, toneMapped: false }),
    );
    this.halo = new THREE.Sprite(
      new THREE.SpriteMaterial({
        map: glowTexture(),
        color: GLOW,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      }),
    );
    this.halo.scale.set(70, 70, 1);
    // свет снаряда есть всегда (яркость 0, когда не летит): число источников не меняется — шейдеры не пересобираются
    this.light = new THREE.PointLight(GLOW, 0, 320, 1.6);
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.BufferAttribute(this.positions, 3));
    this.trail = new THREE.Points(
      geometry,
      new THREE.PointsMaterial({
        size: 5,
        color: 0xb8a8ff,
        transparent: true,
        opacity: 0.85,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      }),
    );
    this.trail.frustumCulled = false;
    scene.add(this.core, this.halo, this.light, this.trail);
    this.hide();
  }

  private hide(): void {
    this.core.visible = false;
    this.halo.visible = false;
    this.trail.visible = false;
    this.light.intensity = 0;
  }

  update(shot: ShotView, tick: number): void {
    if (!shot.live) {
      this.hide();
      return;
    }
    const direction = shot.vx >= 0 ? 1 : -1;
    const pulse = 1 + 0.15 * Math.sin(tick * 0.9);
    this.core.visible = true;
    this.halo.visible = true;
    this.trail.visible = true;
    this.core.position.set(shot.x, shot.y, 0);
    this.core.scale.setScalar(pulse);
    this.halo.position.set(shot.x, shot.y, 0);
    this.halo.scale.set(70 * pulse, 70 * pulse, 1);
    this.light.position.set(shot.x, shot.y, 30);
    this.light.intensity = 6000 * Math.min(1, (shot.age + 1) / 4);
    // хвост: искры позади по пути, расходятся и гаснут к концу
    for (let index = 0; index < TRAIL; index++) {
      const seed = hash(index * 7919 + tick * 31);
      const back = (index / TRAIL) * Math.min(160, 12 * (shot.age + 1));
      const spread = (index / TRAIL) * 14;
      this.positions[index * 3] = shot.x - direction * back;
      this.positions[index * 3 + 1] = shot.y + (((seed & 255) / 255) * 2 - 1) * spread;
      this.positions[index * 3 + 2] = ((((seed >>> 8) & 255) / 255) * 2 - 1) * spread;
    }
    this.trail.geometry.getAttribute("position").needsUpdate = true;
  }
}
